/*
 * The sticky call to action.
 *
 * It appears once the hero's reg finder has scrolled away and stands down again
 * as soon as another one is on screen — so there is never a floating "check
 * your car" bar sitting on top of the field it is asking you to use.
 *
 * The price is mirrored from the hero finder rather than fetched again. That
 * figure is written by the fitment engine from the live index, and copying it
 * is what guarantees the bar can never quote a number the page above it has
 * already moved on from.
 */
(() => {
  if (customElements.get('airflux-sticky-cta')) return;

  class AirfluxStickyCta extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-jump]');
      this.priceOut = this.querySelector('[data-sticky-price]');

      // Every place a customer can act is a reason to stand down.
      //
      // The markup marks those explicitly rather than letting this file guess
      // at `airflux-fitment`: on the landing page that element wraps the
      // gallery as well as the controls, so watching it would keep the bar down
      // for the whole first screen and a half — long after the field itself had
      // scrolled away.
      this.finders = [...document.querySelectorAll('[data-airflux-surface]')];
      if (!this.finders.length) return;

      this.visible = new Set();
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.visible.add(entry.target);
            else this.visible.delete(entry.target);
          }
          this.toggleAttribute('data-shown', this.visible.size === 0);
        },
        // A finder half off the bottom of the screen still counts as available,
        // so the bar does not flicker in on the way past it.
        { rootMargin: '-8% 0px -8% 0px', threshold: 0 }
      );

      for (const finder of this.finders) this.observer.observe(finder);

      this.mirrorPrice();
      this.mirrorQuantity();
      this.button?.addEventListener('click', () => (this.buyable ? this.add() : this.jump()));

      // The landing pages' bar becomes a real buy bar once the page has
      // resolved to something buyable — the standard mobile product-page
      // pattern, and on a phone the most valuable control on the page.
      //
      // The product page's bar posts its own form and must not be touched, so
      // this only binds where there is no form to conflict with.
      if (!this.querySelector('.af-sticky__form')) {
        this.onResolved = this.onResolved.bind(this);
        document.addEventListener('airflux:resolved', this.onResolved);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.priceWatcher?.disconnect();
      if (this.onResolved) document.removeEventListener('airflux:resolved', this.onResolved);
    }

    /**
     * Swap between asking and buying.
     *
     * Nothing about the product is stored — only whether there IS one. The
     * button in the buy column is rebuilt on every chip click, so the bar looks
     * it up at the moment of the press and can never hold a stale variant.
     */
    onResolved(event) {
      const detail = event.detail ?? {};
      this.buyable = !!detail.buyable;

      const label = this.querySelector('[data-sticky-label]');
      const note = this.querySelector('[data-sticky-note]');
      if (label) label.textContent = this.dataset[this.buyable ? 'labelBuy' : 'labelAsk'] ?? label.textContent;
      // The car, out of context — scrolled six screens into the fitting steps,
      // "Audi A3 · 3-door Hatchback" is what says this bar is about their car.
      if (note) note.textContent = (this.buyable && detail.car) || this.dataset.noteAsk || '';

      for (const glyph of this.querySelectorAll('[data-sticky-glyph]'))
        glyph.hidden = glyph.dataset.stickyGlyph !== (this.buyable ? 'buy' : 'ask');
    }

    /**
     * Presses the buy column's own button.
     *
     * Deliberately not a second add-to-cart: that one already carries the
     * fitment line-item properties the client fulfils from (§5.3.6), and a
     * duplicate here would be a second place for them to go wrong. If the
     * column has been repainted out from under us, fall back to scrolling.
     */
    add() {
      const target = document.querySelector('[data-airflux-add]');
      if (!target) return this.jump();
      target.click();

      const label = this.querySelector('[data-sticky-label]');
      if (!label) return;
      const original = label.textContent;
      // Mirrors the real button's own state rather than inventing a timeline,
      // so the two can never disagree about whether the add succeeded.
      const targetLabel = target.querySelector('.af-btn__label') ?? target;
      const watcher = new MutationObserver(() => {
        label.textContent = targetLabel.textContent;
        if (targetLabel.textContent === original) watcher.disconnect();
      });
      watcher.observe(targetLabel, { childList: true, characterData: true, subtree: true });
      setTimeout(() => watcher.disconnect(), 8000);
    }

    /** Keep the bar's figure equal to the hero finder's, whenever that lands. */
    mirrorPrice() {
      const source = document.querySelector('[data-airflux-price]');
      if (!source || !this.priceOut) return;

      // 🔴 Read the PARTS, never the element's text.
      //
      // `[data-airflux-price]` is the price element on a product page but the
      // whole three-part ROW on a landing page — where the engine paints it —
      // so `textContent` ran the three together and the bar read
      // "Was £43.90£34.90Save 21%".
      //
      // The bar shows two of the three: the reference price struck through and
      // the price you pay. The savings badge is dropped on purpose — it is a
      // third element competing for a strip that is already carrying a title, a
      // car and a button, and the discount is legible from the pair alone.
      const copy = () => {
        // Look up from the block, not from the hook. `[data-airflux-price]` is
        // the whole ROW on a landing page and the price ELEMENT on a product
        // page — querying the hook directly found the reference price on one and
        // not the other, so the bar showed the comparison on landing pages only.
        const root = source.closest('.af-price') ?? source;
        const now = root.querySelector('.af-price__now') ?? source;
        const nowText = (now.textContent || '').trim();
        // The `<s>` alone. `.af-price__was` also holds a visually-hidden "Was "
        // for screen readers reading the main price; copied as text it became a
        // visible word in a strip with no room for one.
        const wasText = (root.querySelector('.af-price__was s')?.textContent || '').trim();

        // Built as nodes rather than markup: these strings come from the money
        // formatter, and the bar has no business interpreting them as HTML.
        this.priceOut.textContent = '';
        if (wasText && nowText) {
          // The bar's own hidden label, so a screen reader still hears what the
          // strikethrough means rather than two bare numbers.
          const sr = document.createElement('span');
          sr.className = 'af-visually-hidden';
          sr.textContent = 'Was ';
          const was = document.createElement('s');
          was.className = 'af-sticky__was';
          was.textContent = wasText;
          this.priceOut.append(sr, was, ' ');
        }
        this.priceOut.append(nowText);
        this.priceOut.toggleAttribute('hidden', !nowText);
      };

      copy();
      this.priceWatcher = new MutationObserver(copy);
      this.priceWatcher.observe(source, { childList: true, characterData: true, subtree: true });
    }

    /**
     * Keep the bar's hidden quantity equal to the buy box's selector.
     *
     * Only the product page has both — the landing pages have no quantity and no
     * form here, so this finds nothing and does nothing.
     *
     * It has to be mirrored rather than shared: the bar posts its OWN form (see
     * the section file for why), and a form only submits the fields inside it.
     * Without this the bar sent no `quantity`, Shopify defaulted to 1, and a
     * customer who had set 3 in the buy box got one — with the selector still
     * reading 3 when they scrolled back up.
     */
    mirrorQuantity() {
      const out = this.querySelector('[data-sticky-quantity]');
      const source = document.querySelector('.quantity__input');
      if (!out || !source) return;

      const copy = () => {
        // Guard the floor: an empty or sub-minimum field would otherwise post a
        // quantity Shopify rejects, and the bar has no error surface to show it.
        const n = parseInt(source.value, 10);
        const min = parseInt(source.min, 10) || 1;
        out.value = String(Number.isFinite(n) && n >= min ? n : min);
      };

      copy();
      // `change` covers typing and the stepper's own dispatch; `input` covers
      // the +/- buttons, which set `.value` and fire input on some browsers.
      source.addEventListener('change', copy);
      source.addEventListener('input', copy);
    }

    /** Take them to the nearest finder and put the cursor in the plate. */
    jump() {
      const target = this.finders[0];
      target.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });

      // Focusing mid-scroll fights the scroll on iOS, so it waits for the
      // element to actually arrive and gives up rather than hanging on.
      const input = target.querySelector('[data-airflux-reg]');
      if (!input) return;
      let tries = 0;
      const settle = () => {
        const box = target.getBoundingClientRect();
        if ((box.top > 0 && box.bottom < innerHeight) || tries > 40) {
          input.focus({ preventScroll: true });
          return;
        }
        tries += 1;
        requestAnimationFrame(settle);
      };
      requestAnimationFrame(settle);
    }
  }

  customElements.define('airflux-sticky-cta', AirfluxStickyCta);
})();
